import { ProductionCostInfo } from "./ProductionCostInfo";
import { productionOverheadLines } from "@/lib/productionCostOverhead";
import { PendingMaterialConfirmations } from './PendingMaterialConfirmations';
import { ProductionMaterialPlanning } from './ProductionMaterialPlanning';
import { colorVariantFields } from "@/lib/materialColors";
import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { CostSheetManager } from "./CostSheetManager";
import { Bell, Loader2, Plus, RefreshCw, Save, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { fetchFamilies, fetchSeasons, formatMoney } from "@/lib/api/manufacturing";
import {
  deleteProductionOrder,
  fetchProductionOrder,
  fetchProductionOrderTree,
  fetchPendingProductionDocuments,
  PRODUCTION_ORDER_SOURCE_LABELS,
  saveProductionOrder,
} from "@/lib/api/mfgProductionOrders";
import { SIMULATION_SIZES } from "@/lib/constants";
import { cellKey } from "@/components/manufacturing/orders/OrderSizeColorMatrix";
import { ProductionOrderTree } from "./ProductionOrderTree";
import { SuborderProductionForm, ArticleImage } from "./SuborderProductionForm";
import { validateProcessTracking } from "@/lib/manufacturingProcessTracking";

function DetailContainer({ editing, title, saving, onClose, children }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-[1800px] max-h-[95vh] grid-cols-[minmax(0,1fr)] overflow-x-hidden overflow-y-auto">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{editing ? "Modifica las fechas, cantidades y proveedores de la S/O. Al cerrar se guardan los cambios." : "Consulta los datos de la orden de fabricación y sus subórdenes."}</DialogDescription>
        {children}
      </DialogContent>
    </Dialog>
  );
}

const SIZE_FIELDS = {
  P: "quantity_p",
  M: "quantity_m",
  L: "quantity_l",
  XL: "quantity_xl",
  "2XL": "quantity_2xl",
  "3XL": "quantity_3xl",
  "4XL": "quantity_4xl",
  "5XL": "quantity_5xl",
};

function sortProducts(products = []) {
  return [...products].sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
}

function sortColorLines(lines = []) {
  return [...lines].sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0) || String(left.color || "").localeCompare(String(right.color || "")));
}

function sortCostLines(lines = []) {
  return [...lines].sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0) || String(left.name_snapshot || "").localeCompare(String(right.name_snapshot || "")));
}

function normalizeProductForEdit(product) {
  const colorLines = sortColorLines(product.color_lines || []);
  const colors = colorLines.map((line) => line.color).filter(Boolean);
  const quantities = {};
  const contrasts = {};
  for (const line of colorLines) {
    contrasts[line.color] = {
      contrast_1: line.contrast_1 || "",
      contrast_2: line.contrast_2 || "",
      contrast_3: line.contrast_3 || "",
    };
    for (const size of SIMULATION_SIZES) {
      quantities[cellKey(line.color, size)] = Number(line[SIZE_FIELDS[size]] || 0);
    }
  }
  return {
    ...product,
    color_lines: colorLines,
    cost_lines: sortCostLines(product.cost_lines || []),
    editColors: colors,
    editQuantities: quantities,
    editContrasts: contrasts,
  };
}

function normalizeProductsForEdit(products = []) {
  return sortProducts(products).map(normalizeProductForEdit);
}

function quantityTotal(product) {
  return (product.editColors || []).reduce(
    (sum, color) => sum + SIMULATION_SIZES.reduce((sizeSum, size) => sizeSum + Number(product.editQuantities?.[cellKey(color, size)] || 0), 0),
    0
  );
}

function buildColorLines(product) {
  return (product.editColors || []).map((color, index) => ({
    ...colorVariantFields((product.color_lines || []).find((line) => line.color === color) || { color }),
    color,
    contrast_1: product.editContrasts?.[color]?.contrast_1?.trim() || null,
    contrast_2: product.editContrasts?.[color]?.contrast_2?.trim() || null,
    contrast_3: product.editContrasts?.[color]?.contrast_3?.trim() || null,
    quantity_p: Number(product.editQuantities?.[cellKey(color, "P")] || 0),
    quantity_m: Number(product.editQuantities?.[cellKey(color, "M")] || 0),
    quantity_l: Number(product.editQuantities?.[cellKey(color, "L")] || 0),
    quantity_xl: Number(product.editQuantities?.[cellKey(color, "XL")] || 0),
    quantity_2xl: Number(product.editQuantities?.[cellKey(color, "2XL")] || 0),
    quantity_3xl: Number(product.editQuantities?.[cellKey(color, "3XL")] || 0),
    quantity_4xl: Number(product.editQuantities?.[cellKey(color, "4XL")] || 0),
    quantity_5xl: Number(product.editQuantities?.[cellKey(color, "5XL")] || 0),
    sort_order: index,
  }));
}

function recalculateCostLines(product, totalQuantity) {
  return (product.cost_lines || []).map((line) => {
    const quantity = Number(line.quantity_per_unit || 0);
    const unitCost = Number(line.unit_cost || 0);
    const wastePct = Number(line.waste_pct || 0);
    const calculatedUnitCost = line.line_type === "material"
      ? quantity * (1 + wastePct / 100) * unitCost
      : quantity * unitCost;
    const previousUnitCost = Number(product.total_quantity || 0) > 0
      ? Number(line.total_cost || 0) / Number(product.total_quantity || 0)
      : 0;
    const unitLineCost = calculatedUnitCost || previousUnitCost;
    return {
      ...line,
      total_cost: Number((unitLineCost * totalQuantity).toFixed(4)),
    };
  });
}

function matchesText(value, term) {
  const search = String(term || "").trim().toLowerCase();
  if (!search) return true;
  return String(value || "").toLowerCase().includes(search);
}

function matchesOrderProductFilters(product, articleFilter, suborderFilter) {
  const articleOk = !String(articleFilter || "").trim()
    || [product.product_code_snapshot, product.description_snapshot].some((value) => matchesText(value, articleFilter));
  const suborderOk = matchesText(product.subnumber, suborderFilter);
  return articleOk && suborderOk;
}

function FilterField({ label, value, onChange, placeholder }) {
  const hasValue = Boolean(String(value || "").trim());
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input className="pl-8 pr-9" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
        {hasValue && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            onClick={() => onChange("")}
            title="Quitar filtro"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function buildProductsForSave(products = []) {
  return products.map((product) => {
    const totalQuantity = quantityTotal(product);
    const unitProcessCost = Number(product.unit_process_cost || 0);
    const unitMaterialCost = Number(product.unit_material_cost || 0);
    const unitTotalCost = Number(product.unit_total_cost || unitProcessCost + unitMaterialCost || 0);
    return {
      ...product,
      total_quantity: totalQuantity,
      total_process_cost: Number((unitProcessCost * totalQuantity).toFixed(4)),
      total_material_cost: Number((unitMaterialCost * totalQuantity).toFixed(4)),
      total_cost: Number((unitTotalCost * totalQuantity).toFixed(4)),
      color_lines: buildColorLines(product),
      cost_lines: recalculateCostLines(product, totalQuantity),
    };
  });
}

export function ProductionOrderList({ reloadKey = 0, onCreate }) {
  const [orders, setOrders] = React.useState([]);
  const [families, setFamilies] = React.useState([]);
  const [seasons, setSeasons] = React.useState([]);
  const [focusedSuborderId, setFocusedSuborderId] = React.useState(null);
  const [costSheetProduct, setCostSheetProduct] = React.useState(null);
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const detailRef = React.useRef(null);
  React.useEffect(() => {
    if (selectedOrder && new URLSearchParams(window.location.hash.split('?')[1]).get('from') === 'dashboard') detailRef.current?.scrollIntoView({ block: 'start' });
  }, [selectedOrder?.id]);
  const [detailMode, setDetailMode] = React.useState("view");
  const [draftProducts, setDraftProducts] = React.useState([]);
  const [articleFilter, setArticleFilter] = React.useState("");
  const [suborderFilter, setSuborderFilter] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");
  const [seasonFilter, setSeasonFilter] = React.useState("");
  const [hideCompleted, setHideCompleted] = React.useState(true);
  const [sourceFilter, setSourceFilter] = React.useState("");
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const [pendingCount, setPendingCount] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const listRequestRef = React.useRef(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [savingDetail, setSavingDetail] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState(null);
  const { toast } = useToast();

  React.useEffect(() => {
    const loadMasters = async () => {
      const [familiesRes, seasonsRes] = await Promise.all([fetchFamilies(), fetchSeasons()]);
      setFamilies(familiesRes.data || []);
      setSeasons(seasonsRes.data || []);
    };
    loadMasters();
  }, []);

  const loadOrders = React.useCallback(async () => {
    const request = ++listRequestRef.current;
    setLoading(true);
    const [result, pending] = await Promise.all([fetchProductionOrderTree({
      searchTerm,
      codFam: familyFilter,
      temporada: seasonFilter,
      status: hideCompleted ? "incomplete" : "",
      sourceType: sourceFilter,
      limit: 101, offset: page * 100, pendingOnly,
    }), fetchPendingProductionDocuments()]);
    if (request !== listRequestRef.current) return;
    setLoading(false);
    setPendingCount(pending.error ? null : pending.data.reduce((sum, row) => sum + Number(row.pending_count), 0));
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar las órdenes de fabricación", variant: "destructive" });
      return;
    }
    setOrders((result.data || []).slice(0, 100));
    setHasMore((result.data || []).length > 100);
    setSelectedOrder((current) => ((result.data || []).some((row) => row.id === current?.id) ? current : null));
  }, [familyFilter, searchTerm, seasonFilter, sourceFilter, hideCompleted, pendingOnly, page, toast]);

  React.useEffect(() => { setPage(0); }, [familyFilter, searchTerm, seasonFilter, sourceFilter, hideCompleted, pendingOnly]);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders, reloadKey]);

  React.useEffect(() => {
    const refresh = () => { if (!document.hidden) loadOrders(); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [loadOrders]);

  const openOrder = async (order, mode = "view", expandProductId = null) => {
    setFocusedSuborderId(expandProductId);
    setLoadingDetail(true);
    setDetailMode(mode);
    setArticleFilter("");
    setSuborderFilter("");
    const result = await fetchProductionOrder(order.id);
    setLoadingDetail(false);
    if (result.error || !result.data) {
      toast({ title: "Error", description: "No se pudo cargar el detalle de la orden", variant: "destructive" });
      return;
    }
    const products = normalizeProductsForEdit(result.data.products || []);
    setSelectedOrder({ ...result.data, products });
    setDraftProducts(products);
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1]);
    const id = params.get('order');
    if (!id) return;
    let cancelled = false;
    setLoadingDetail(true);
    setFocusedSuborderId(params.get('suborder'));
    setDetailMode('view');
    fetchProductionOrder(id).then((result) => {
      if (cancelled) return;
      setLoadingDetail(false);
      if (result.error || !result.data) {
        toast({ title: 'Orden no disponible', description: 'No se pudo abrir la orden solicitada.', variant: 'destructive' });
        return;
      }
      const products = normalizeProductsForEdit(result.data.products || []);
      setSelectedOrder({ ...result.data, products });
      setDraftProducts(products);
    });
    return () => { cancelled = true; };
  }, [toast]);

  const handleDeleteOrder = async (order) => {
    if (!order?.id) return;
    if (!window.confirm(`¿Eliminar definitivamente la orden de fabricación ${order.number} y todas sus S/O?\n\nAtención: si hay envíos a acabadores, recepciones o procesos con seguimiento, también se borrarán. Se eliminarán en cascada todos los registros asociados, incluidas matrices, documentos de devolución, facturas y pagos.\n\nEsta acción no se puede deshacer. ¿Continuar con el borrado?`)) return;

    setDeletingId(order.id);
    const result = await deleteProductionOrder(order.id);
    setDeletingId(null);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo eliminar la orden", variant: "destructive" });
      return;
    }

    setOrders((current) => current.filter((item) => item.id !== order.id));
    if (selectedOrder?.id === order.id) {
      setSelectedOrder(null);
      setDetailMode("view");
      setDraftProducts([]);
      setArticleFilter("");
      setSuborderFilter("");
    }
    await loadOrders();
    toast({ title: "Orden eliminada", description: order.number });
  };

  const updateProduct = (productId, updater) => {
    setDraftProducts((current) => current.map((product) => product.id === productId ? updater(product) : product));
  };

  const savingDetailRef = React.useRef(false);
  const handleSaveDetail = async ({ keepOpen = false } = {}) => {
    if (savingDetailRef.current) return;
    savingDetailRef.current = true;
    try {
    if (!selectedOrder?.id) return;
    const items = buildProductsForSave(draftProducts);
    if (!items.some((item) => Number(item.total_quantity || 0) > 0)) {
      toast({ title: "Nada que guardar", description: "La orden debe contener al menos una cantidad.", variant: "destructive" });
      return;
    }

    const invalidDates = items.find((item) => !item.order_date || (item.finished_date && item.finished_date < item.order_date));
    if (invalidDates) {
      toast({ title: "Revisa las fechas", description: `${invalidDates.subnumber}: la creación es obligatoria y la finalización no puede ser anterior.`, variant: "destructive" });
      return;
    }
    const trackingError = validateProcessTracking(items);
    if (trackingError) { toast({ title: "Revisa el seguimiento", description: trackingError, variant: "destructive" }); return; }
    setSavingDetail(true);
    const result = await saveProductionOrder({
      id: selectedOrder.id,
      isSample: selectedOrder.is_sample === true,
      number: selectedOrder.number,
      orderDate: selectedOrder.order_date,
      description: selectedOrder.description || "",
      temporada: selectedOrder.temporada || null,
      deliveryDate: selectedOrder.delivery_date || null,
      observations: selectedOrder.observations || "",
      sourceType: selectedOrder.source_type || "manual",
      sourceSimulationId: selectedOrder.source_simulation_id || null,
      items,
    });
    if (result.error) {
      setSavingDetail(false);
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el detalle", variant: "destructive" });
      return;
    }

    const detail = await fetchProductionOrder(result.data || selectedOrder.id);
    setSavingDetail(false);
    if (detail.error || !detail.data) {
      toast({ title: "Guardado", description: "La orden se guardó, pero no se pudo recargar el detalle." });
      return;
    }

    const products = normalizeProductsForEdit(detail.data.products || []);
    const nextOrder = { ...detail.data, products };
    setSelectedOrder(keepOpen ? nextOrder : null);
    setDraftProducts(products);
    if (!keepOpen) setDetailMode("view");
    await loadOrders();
    if (!keepOpen) toast({ title: "Orden actualizada", description: focusedSuborderId ? products.find((product) => product.id === focusedSuborderId)?.subnumber : nextOrder.number });
    return nextOrder;
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudieron guardar los cambios", variant: "destructive" });
    } finally {
      savingDetailRef.current = false;
      setSavingDetail(false);
    }
  };

  const isEditing = detailMode === "edit";
  const filteredProducts = React.useMemo(
    () => draftProducts.filter((product) => matchesOrderProductFilters(product, articleFilter, suborderFilter)),
    [articleFilter, draftProducts, suborderFilter]
  );
  const focusedProduct = draftProducts.find((p) => p.id === focusedSuborderId);
  const visibleDetailProducts = (isEditing ? filteredProducts : draftProducts).filter((p) => !focusedSuborderId || p.id === focusedSuborderId);
  const hasDetailFilters = Boolean(articleFilter.trim() || suborderFilter.trim());

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Órdenes reales de fabricación</h2>
            <p className="text-sm text-gray-600">Listado diario por S/O. El número de OF se usa solo para agrupar.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" aria-pressed={pendingOnly}
              className={pendingCount > 0 ? "border-amber-400 bg-amber-50 text-amber-900" : ""}
              onClick={() => setPendingOnly((current) => !current)}
              title="Filtrar S/O con documentos pendientes de validar e incorporar">
              <Bell className="mr-2 h-4 w-4" /><span aria-live="polite">{pendingCount === null ? "Documentos pendientes: sin datos" : `${pendingCount} docs pendientes de incorporar`}</span>
            </Button>
            <PendingMaterialConfirmations />
            {onCreate && (
              <Button type="button" onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />Nueva OF directa
              </Button>
            )}
            <Button type="button" variant="outline" onClick={loadOrders} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />Recargar
            </Button>
          </div>
        </div>

        <div className="mb-4 grid gap-2 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_180px_220px_170px_180px]">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-8"
              placeholder="Buscar por OF, S/O, artículo o descripción"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <select className="rounded-md border bg-white px-3 py-2 text-sm" value={familyFilter} onChange={(event) => setFamilyFilter(event.target.value)}>
            <option value="">Todas las familias</option>
            {families.map((family) => (
              <option key={family.cod_fam} value={family.cod_fam.trim()}>
                {family.cod_fam.trim()} · {family.descrip}
              </option>
            ))}
          </select>
          <SeasonSelect data-season-selector="true" className="rounded-md border bg-white px-3 py-2 text-sm" value={seasonFilter} onChange={(event) => setSeasonFilter(event.target.value)}>
            <option value="">Todas las temporadas</option>
            {seasons.map((season) => (
              <option key={season.cod_tem} value={season.cod_tem.trim()}>
                {season.cod_tem.trim()} · {season.descrip}
              </option>
            ))}
          </SeasonSelect>

          <select className="rounded-md border bg-white px-3 py-2 text-sm" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
            <option value="">Todos los orígenes</option>
            {Object.entries(PRODUCTION_ORDER_SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <label className="mb-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={hideCompleted} onChange={(event) => setHideCompleted(event.target.checked)} />
          Ocultar S/O terminadas
        </label>
        <ProductionOrderTree orders={orders} loading={loading} selectedOrderId={selectedOrder?.id}
          deletingId={deletingId} onOpen={openOrder} onDelete={handleDeleteOrder} />
      </div>

      {loadingDetail && <div className="rounded-lg border bg-white p-4 text-sm text-gray-600 shadow-sm">Cargando detalle...</div>}

      <div className="flex items-center justify-end gap-3"><Button variant="outline" disabled={!page || loading} onClick={() => setPage((p) => p - 1)}>Anterior</Button><span>Página {page + 1}</span><Button variant="outline" disabled={!hasMore || loading} onClick={() => setPage((p) => p + 1)}>Siguiente</Button></div>
      {selectedOrder && !loadingDetail && (
        <DetailContainer
          editing={isEditing}
          title={`${isEditing ? "Editar" : "Consultar"} ${focusedProduct?.subnumber || selectedOrder.number}`}
          saving={savingDetail}
          onClose={() => {
            if (isEditing) return handleSaveDetail();
            setSelectedOrder(null);
            setDraftProducts([]);
            setFocusedSuborderId(null);
            setCostSheetProduct(null);
          }}
        >
        <div ref={detailRef} className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              {focusedProduct && <ArticleImage product={focusedProduct.product} />}
              <div><h3 className="text-lg font-semibold">{focusedProduct ? (focusedProduct.product_code_snapshot || focusedProduct.product?.code) : selectedOrder.number} · {focusedProduct?.description_snapshot || selectedOrder.description || "Orden de fabricación"}</h3>
              <p className="text-sm text-gray-600">{selectedOrder.observations || "Sin observaciones"}</p>
              <label className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium">Tipo de fabricación:
                {isEditing ? <select aria-label="Tipo de fabricación" className="rounded border bg-white px-3 py-2 disabled:bg-gray-50"
                  disabled={!isEditing || savingDetail} value={selectedOrder.is_sample ? 'samples' : 'standard'}
                  onChange={(event) => setSelectedOrder(current => ({ ...current, is_sample: event.target.value === 'samples' }))}>
                  <option value="standard">Normal</option><option value="samples">Muestras</option>
                </select> : <span>{selectedOrder.is_sample ? 'Muestras' : 'Normal'}</span>}
              </label>
              <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{isEditing ? "Edición" : "Consulta"}</p></div>
            </div>
            <div className="flex flex-col gap-2 md:min-w-[460px]">
              <div className="grid grid-cols-2 gap-2 text-right text-sm">
                <div className="rounded border bg-gray-50 px-3 py-2"><span className="block text-xs uppercase text-gray-500">Prendas</span><b>{focusedProduct?.total_quantity ?? selectedOrder.total_quantity}</b></div>
                <div className="rounded border bg-blue-50 px-3 py-2"><span className="block text-xs uppercase text-blue-700">Coste de producción <ProductionCostInfo lines={productionOverheadLines(focusedProduct ? [focusedProduct] : selectedOrder.products)} /></span><b className="text-blue-900">{formatMoney(focusedProduct?.total_cost ?? selectedOrder.grand_total)}</b></div>
              </div>
              {focusedProduct && (
                <Button type="button" variant="outline" disabled={!focusedProduct.product_id || savingDetail} onClick={() => setCostSheetProduct(focusedProduct)}>
                  Ver escandallo del artículo
                </Button>
              )}
              {isEditing && (
                <Button type="button" onClick={handleSaveDetail} disabled={savingDetail || !draftProducts.length}>
                  {savingDetail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Guardar cambios de S/O
                </Button>
              )}
            </div>
          </div>

          <ProductionMaterialPlanning key={selectedOrder.id} order={selectedOrder} editing={isEditing} beforeClose={isEditing ? () => handleSaveDetail({ keepOpen: true }) : undefined} />

          {isEditing && !focusedSuborderId && draftProducts.length > 1 && (
            <div className="mb-4 grid gap-3 md:grid-cols-2">
              <FilterField
                label="Filtrar por subfabricación"
                value={suborderFilter}
                onChange={setSuborderFilter}
                placeholder="Número de suborden"
              />
              <FilterField
                label="Filtrar por artículo"
                value={articleFilter}
                onChange={setArticleFilter}
                placeholder="Código o descripción"
              />
              <p className="text-xs text-gray-500 md:col-span-2">
                {hasDetailFilters
                  ? `Mostrando ${filteredProducts.length} de ${draftProducts.length} subórdenes`
                  : `${draftProducts.length} subórdenes`}
              </p>
            </div>
          )}

          <div className="space-y-5">
            {visibleDetailProducts.map((product) => <SuborderProductionForm key={product.id} order={selectedOrder} product={product}
              editing={isEditing} disabled={savingDetail} showHeading={!focusedProduct}
              onViewCostSheet={!focusedProduct ? setCostSheetProduct : undefined}
              updateProduct={(updater) => updateProduct(product.id, updater)}
              beforeAction={async () => {
                const saved = await handleSaveDetail({ keepOpen: true });
                if (!saved) throw new Error("No se pudieron guardar los cambios de la S/O.");
                return saved.products.find((item) => item.id === product.id);
              }}
              onReloaded={async () => {
                const result = await fetchProductionOrder(selectedOrder.id);
                if (result.error || !result.data) throw result.error || new Error("No se pudo recargar la S/O");
                const products = normalizeProductsForEdit(result.data.products || []);
                setSelectedOrder({ ...result.data, products }); setDraftProducts(products);
                await loadOrders();
              }} />)}
          </div>
        </div>
        <Dialog open={Boolean(costSheetProduct)} onOpenChange={(open) => { if (!open) setCostSheetProduct(null); }}>
          <DialogContent className="w-[95vw] max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogTitle>Escandallo · {costSheetProduct?.product_code_snapshot}</DialogTitle>
            <DialogDescription>Consulta del escandallo actual del artículo. Al cerrar volverás a {focusedProduct ? 'la S/O' : 'la vista agrupada de la orden'}.</DialogDescription>
            {costSheetProduct && <CostSheetManager key={costSheetProduct.product_id} productId={costSheetProduct.product_id} hideProductSelector readOnly />}
          </DialogContent>
        </Dialog>
        </DetailContainer>
      )}
    </div>
  );
}
